import { z } from 'zod';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { PositionSchema } from '../../shared/position.schema';
import { EntityOrSuggestionRefSchema } from '../../entities/entity-suggestion';
/**
 * POST /api/v2/process/:id/draft/steps/:stepId/successor-steps
 *
 * Manual-parent only: materialize each picked successor as a downstream
 * `ProcessDraftAssignment` linked from the parent via `inputs[].sourceSteps`,
 * and merge the same entities into the parent's `task.successors.*` lists.
 * Existing entries are skipped (idempotent — no duplicate child nodes).
 *
 * `orgFunctions` and `systems` carry the {@link EntityOrSuggestionRefSchema} union
 * so a suggested-but-unapproved orgFunction/system materializes a draft node bound
 * to `assignedTo: { type: 'suggestion', id }`; approval later rewrites it in
 * place. `persons`/`externalContacts` have no suggest-new and stay plain ObjectIds.
 *
 * `position` seeds each newly-created assignment's entry in the draft-scoped
 * `ProcessLayout` doc. Every created assignment shares this base position —
 * the client-side swimlane layout reassigns Y per assignee's lane at render
 * time. Without this, sidebar-initiated successor creation would drop new
 * nodes at `{0, 0}`.
 *
 * `ruleId` scopes the call to a decision parent: the new assignment is
 * anchored to that rule's output handle via `sourceSteps[0].ruleIds`, and
 * `rule.successors` is updated instead of `task.successors`. Required when
 * the parent is a decision step; rejected otherwise.
 *
 * The response envelope carries the updated parent and every created
 * assignment in `nodes`, with the created assignments' `stableStepId`s in
 * `createdNodeIds`.
 */ export const AddSuccessorStepsRequestSchema = z.object({
    persons: z.array(ObjectIdSchema).optional(),
    orgFunctions: z.array(EntityOrSuggestionRefSchema).optional(),
    systems: z.array(EntityOrSuggestionRefSchema).optional(),
    externalContacts: z.array(ObjectIdSchema).optional(),
    position: PositionSchema.optional(),
    ruleId: ObjectIdSchema.optional()
});
/** The four assignee buckets a picker emits, without the placement fields. */ export const AssigneeBucketsSchema = AddSuccessorStepsRequestSchema.pick({
    persons: true,
    orgFunctions: true,
    systems: true,
    externalContacts: true
});

//# sourceMappingURL=add-successor-steps.schema.js.map